import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, User, Stethoscope, MapPin } from 'lucide-react';
import { Button } from '@/components/ds';
import { startRinging, stopRinging } from '@/utils/ringSound';

interface PaymentPendingModalProps {
  isOpen: boolean;
  patientName: string;
  doctorName: string;
  branchName?: string;
  onConfirm: () => void;
}

export const PaymentPendingModal = ({
  isOpen,
  patientName,
  doctorName,
  branchName,
  onConfirm,
}: PaymentPendingModalProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      startRinging();
    } else {
      stopRinging();
    }
    return () => stopRinging();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    stopRinging();
    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in zoom-in-95 duration-200">
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <DollarSign className="w-10 h-10 text-success-500" />
          </div>

          <h2 className="text-2xl font-bold text-neutral-800 mb-2">
            {t('notifications.paymentPending')}
          </h2>

          <p className="text-neutral-600 mb-6">
            {t('notifications.paymentPendingDesc')}
          </p>

          <div className="w-full bg-neutral-50 rounded-xl p-4 mb-6 space-y-3">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-info-500" />
              <div className="text-left">
                <p className="text-xs text-neutral-500">
                  {t('common.patient')}
                </p>
                <p className="font-semibold text-neutral-800">{patientName}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Stethoscope className="w-5 h-5 text-success-500" />
              <div className="text-left">
                <p className="text-xs text-neutral-500">
                  {t('notifications.treatedBy')}
                </p>
                <p className="font-semibold text-neutral-800">
                  {t('notifications.paymentPendingModal.dr')} {doctorName}
                </p>
              </div>
            </div>

            {branchName && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-lavender-500" />
                <div className="text-left">
                  <p className="text-xs text-neutral-500">{t('common.room')}</p>
                  <p className="font-semibold text-neutral-800">{branchName}</p>
                </div>
              </div>
            )}
          </div>

          <Button
            onPress={handleConfirm}
            className="w-full bg-success-500 hover:bg-success-600 text-white py-3 text-lg font-semibold"
          >
            {t('notifications.acknowledge')}
          </Button>
        </div>
      </div>
    </div>
  );
};
